import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, Switch, Button, Alert, CircularProgress, MenuItem,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../../constants";
import { loyaltyFetch } from "../../../hooks/loyaltyFetch";
import CouponTypeRewardsLinker from "./CouponTypeRewardsLinker";

// Calendar-aware: shift today by N months/years and count real calendar days
const calendarDaysFromToday = (amount, unit) => {
    const n = Math.max(1, Math.floor(Number(amount)));
    if (unit === "days") return n;

    const today = new Date();
    const future = new Date(today);
    if (unit === "years") future.setFullYear(future.getFullYear() + n);
    else if (unit === "months") future.setMonth(future.getMonth() + n);
    return Math.round((future - today) / (1000 * 60 * 60 * 24));
};

const EditCouponTypeDialog = ({ open, couponType, onClose, onSaved }) => {
    const [form, setForm] = useState({ name: "", description: "", validity_days: "", active: true });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [unitQty, setUnitQty] = useState("");
    const [unitType, setUnitType] = useState("days");

    useEffect(() => {
        if (couponType) {
            setForm({
                name: couponType.name || "",
                description: couponType.description || "",
                validity_days: couponType.validity_days ?? "",
                active: couponType.active ?? true,
            });
            setError(null);
            if (couponType.validity_days) {
                setUnitQty(String(couponType.validity_days));
                setUnitType("days");
            } else {
                setUnitQty("");
                setUnitType("days");
            }
        }
    }, [couponType]);

    const handleChange = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    const isValid = form.name.trim();

    const handleSubmit = async () => {
        if (!isValid || !couponType) return;
        setSubmitting(true);
        setError(null);
        try {
            const body = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                validity_days: form.validity_days !== "" ? parseInt(form.validity_days, 10) : null,
                active: form.active,
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/coupon-types/${couponType.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "X-Brand": couponType.brand },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!couponType) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rewards.gradient }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier le Type de Coupon
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{couponType.name}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom du type de coupon" value={form.name}
                            onChange={handleChange("name")} size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handleChange("description")} size="small" multiline rows={2}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif" mb={0.75} color="#1a1a2e">
                            Validité
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" type="number" label="Quantité"
                                    value={unitQty}
                                    placeholder="Illimité (laisser vide)"
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, "");
                                        setUnitQty(val);
                                        if (val) {
                                            setForm(p => ({ ...p, validity_days: String(calendarDaysFromToday(val, unitType)) }));
                                        } else {
                                            setForm(p => ({ ...p, validity_days: "" }));
                                        }
                                    }}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth select size="small" label="Unité"
                                    value={unitType}
                                    onChange={(e) => {
                                        const newUnit = e.target.value;
                                        setUnitType(newUnit);
                                        if (unitQty) {
                                            setForm(p => ({ ...p, validity_days: String(calendarDaysFromToday(unitQty, newUnit)) }));
                                        }
                                    }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                                    <MenuItem value="days">Jours</MenuItem>
                                    <MenuItem value="months">Mois</MenuItem>
                                    <MenuItem value="years">Années</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">
                                Actif
                            </Typography>
                            <Switch checked={form.active}
                                onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rewards.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rewards.main },
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                <CouponTypeRewardsLinker
                    couponType={couponType}
                    brandName={couponType.brand}
                    onSaved={onSaved}
                />

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.rewards.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditCouponTypeDialog;
